import { createGlobalStyle } from "styled-components";

export const GlobalStyles = createGlobalStyle`
  /* Reset */
  *, *::before, *::after {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    scroll-behavior: smooth;
    font-size: 16px;
    overflow-x: hidden;

    /* Responsive font scaling */
    @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
      font-size: 15px;
    }
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      font-size: 14px;
    }
    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      font-size: 13px;
    }
    @media (max-width: ${({ theme }) => theme.breakpoints.xs}) {
      font-size: 12px;
    }
  }

  body {
    font-family: ${({ theme }) => theme.fonts.primary};
    background-color: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.textPrimary};
    line-height: 1.6;
    overflow-x: hidden;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;

    @media (hover: none) and (pointer: coarse) {
      cursor: auto !important;
    }
  }

  /* Custom cursor for desktops */
  @media (hover: hover) and (pointer: fine) {
    body {
      cursor: none;
    }
    .cursor {
      position: fixed;
      width: 8px;
      height: 8px;
      background: ${({ theme }) => theme.colors.primary};
      border-radius: 50%;
      pointer-events: none;
      z-index: 9999;
      mix-blend-mode: difference;
      transition: transform 0.1s ease;
      transform: translate(-50%, -50%);
    }
    .cursor-follower {
      position: fixed;
      width: 40px;
      height: 40px;
      border: 2px solid ${({ theme }) => theme.colors.primary};
      border-radius: 50%;
      pointer-events: none;
      z-index: 9998;
      transition: all 0.15s ease;
      transform: translate(-50%, -50%);
    }
  }
  @media (hover: none) and (pointer: coarse) {
    .cursor,
    .cursor-follower {
      display: none !important;
    }
  }

  /* Scrollbar */
  ::-webkit-scrollbar {
    width: 8px;
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      width: 4px;
    }
  }
  ::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.backgroundSecondary};
  }
  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.primary};
    border-radius: 4px;
    transition: background 0.3s ease;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
  }

  /* Text selection with dark-blue-green gradient background */
  ::selection {
    background: linear-gradient(
      45deg,
      black,
      white,
      ${({ theme }) => theme.colors.primary}
    );
    color: ${({ theme }) => theme.colors.background};
  }

  /* Focus outlines */
  *:focus,
  button:focus-visible,
  a:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }

  /* Links */
  a {
    color: inherit;
    text-decoration: none;
    transition: color 0.3s ease;

    &:hover,
    &:focus-visible {
      color: ${({ theme }) => theme.colors.primary};
      outline: none;
    }
  }

  /* Buttons */
  button {
    border: none;
    background: none;
    font-family: inherit;
    cursor: pointer;
    min-height: 44px;
    min-width: 44px;
    transition: background-color 0.3s ease, color 0.3s ease;

    &:hover,
    &:focus-visible {
      background-color: ${({ theme }) => theme.colors.primary};
      color: ${({ theme }) => theme.colors.background};
      outline: none;
    }

    @media (hover: none) and (pointer: coarse) {
      cursor: auto;
    }
  }

  /* Images */
  img {
    max-width: 100%;
    height: auto;
    display: block;
  }

  /* Headings */
  h1, h2, h3, h4, h5, h6 {
    font-family: ${({ theme }) => theme.fonts.secondary};
    font-weight: ${({ theme }) => theme.fontWeights.bold};
    line-height: 1.2;
    margin-bottom: 0.5em;
    color: ${({ theme }) => theme.colors.textPrimary};
  }
  h1 { font-size: clamp(2rem, 5vw, 4rem); }
  h2 { font-size: clamp(1.5rem, 4vw, 3rem); }
  h3 { font-size: clamp(1.25rem, 3vw, 2rem); }
  h4 { font-size: clamp(1.125rem, 2.5vw, 1.5rem); }

  /* Paragraphs */
  p {
    margin-bottom: 1em;
    line-height: 1.6;
    color: ${({ theme }) => theme.colors.textSecondary};
  }

  /* Container */
  .container {
    width: 100%;
    max-width: ${({ theme }) => theme.grid.container.xl};
    margin: 0 auto;
    padding: 0 ${({ theme }) => theme.spacing.md};

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      padding: 0 ${({ theme }) => theme.spacing.sm};
    }
    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      padding: 0 ${({ theme }) => theme.spacing.xs};
    }
  }

  /* Grid */
  .grid {
    display: grid;
    gap: ${({ theme }) => theme.grid.gap.md};

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      gap: ${({ theme }) => theme.grid.gap.sm};
    }
    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      gap: ${({ theme }) => theme.grid.gap.xs};
    }
  }

  .grid-cols-1 { grid-template-columns: repeat(1, 1fr); }
  .grid-cols-2 { grid-template-columns: repeat(2, 1fr); }
  .grid-cols-3 { grid-template-columns: repeat(3, 1fr); }
  .grid-cols-4 { grid-template-columns: repeat(4, 1fr); }

  /* Responsive grids */
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    .lg\\:grid-cols-1 { grid-template-columns: repeat(1, 1fr); }
    .lg\\:grid-cols-2 { grid-template-columns: repeat(2, 1fr); }
    .lg\\:grid-cols-3 { grid-template-columns: repeat(3, 1fr); }
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    .md\\:grid-cols-1 { grid-template-columns: repeat(1, 1fr); }
    .md\\:grid-cols-2 { grid-template-columns: repeat(2, 1fr); }
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    .sm\\:grid-cols-1 { grid-template-columns: repeat(1, 1fr); }
  }

  /* Flex utilities */
  .flex { display: flex; }
  .flex-col { flex-direction: column; }
  .items-center { align-items: center; }
  .justify-center { justify-content: center; }
  .justify-between { justify-content: space-between; }

  /* Spacing utilities */
  .space-y-4 > * + * { margin-top: ${({ theme }) => theme.spacing.md}; }
  .space-y-2 > * + * { margin-top: ${({ theme }) => theme.spacing.sm}; }

  /* Animations & other styles omitted for brevity, keep as before */

  /* Gradient background example (for a class or body background) */
  .gradient-bg {
    background: linear-gradient(
      135deg,
      black 0%,
      white 50%,
      ${({ theme }) => theme.colors.primary} 100%
    );
  }
`;
